import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { CompteUtilisateur } from '../../../utils/types/CompteUtilisateur';
import UpdateUserModal from '../../modals/wizard/user/UpdateUserModal';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);

interface CompteUtilisateurGridProps {
    comptes: CompteUtilisateur[];
}

const CompteUtilisateurGrid: React.FC<CompteUtilisateurGridProps> = ({ comptes }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [columnDefs] = useState<ColDef<CompteUtilisateur>[]>([
        {
            headerName: '',
            cellRenderer: (params: any) => <UpdateUserModal user={params.data} />,
            pinned: isMobile ? undefined : 'left',
            sortable: true,
            filter: true,
            width: 50,
        },
        { field: 'nomClient', headerName: 'Client', sortable: true, filter: true },
        { field: 'email', headerName: 'Adresse e-mail', sortable: true, filter: true },
        { field: 'role', headerName: 'Rôle', sortable: true, filter: true },
        {
            field: 'actif',
            headerName: 'Actif',
            cellRenderer: (params: any) => (
                <div style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={params.value} />
                </div>
            ),
            cellStyle: { textAlign: 'center' },
            width: 80,
        },
    ]);

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const columnKeys = visibleCols.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        gridRef.current.api.exportDataAsCsv({
            fileName: `comptes_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const rowData: CompteUtilisateur[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = rowData.map((row) => {
            const obj: Record<string, any> = {};
            visibleCols.forEach((col) => {
                const def = col.getColDef();
                const field = def.field;
                const header = def.headerName ?? field ?? '';
                if (!field) return;
                let value = (row as any)[field];
                if (field === 'actif') {
                    value = value ? 'Oui' : 'Non';
                }
                obj[header] = value;
            });
            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Comptes');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `comptes_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact ref={gridRef} theme={myTheme} getRowId={(params) => params.data.userId ?? ''} rowData={comptes} columnDefs={columnDefs} pagination={true} domLayout="normal" defaultColDef={{ resizable: true, editable: false, minWidth: 100, flex: 1 }} onGridReady={(params) => params.api.autoSizeAllColumns()} suppressExcelExport={true} enableCellTextSelection={true} ensureDomOrder={true} animateRows={true} overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>" />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default CompteUtilisateurGrid;
